/**
 * Shared plumbing for the QuickBooks-backed CFO tabs (Cash, P&L,
 * Creditors, Debtors' cousins): one data hook against
 * /api/quickbooks/{path} with force-refresh, plus the read-only
 * provenance header every QBO tab shows.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const fmtGbp = (v) =>
  v == null
    ? "–"
    : `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtMonthKey = (m) => {
  const [y, mo] = String(m).split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-GB", {
    month: "short",
    year: "2-digit",
  });
};

export const fmtDay = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
    : "–";

export function useQboData(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    async (force = false) => {
      force ? setRefreshing(true) : setLoading(true);
      try {
        const { data: d } = await axios.get(
          `${API}/api/quickbooks/${path}${force ? "?refresh=1" : ""}`,
        );
        setData(d);
        setError(null);
        if (force) toast.success("Refreshed from QuickBooks.");
      } catch (e) {
        setError(
          e?.response?.data?.detail ||
            "Couldn't load QuickBooks data - is it connected?",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [path],
  );

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, refreshing, reload: () => load(true) };
}

export function QboHeader({ data, refreshing, onRefresh, testid }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <span className="inline-flex items-center gap-1.5 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border text-emerald-800 bg-emerald-50 border-emerald-200">
        <Lock className="w-2.5 h-2.5" />
        QuickBooks · read-only
        {data?.environment === "sandbox" ? " · sandbox" : ""}
        {data?.company_name ? ` · ${data.company_name}` : ""}
      </span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1B4F8A] transition-colors disabled:opacity-50"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        {data?.generated_at
          ? `As of ${new Date(data.generated_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
          : "Refresh"}
      </button>
    </div>
  );
}
